import { z } from 'zod';
import { TextBloc } from '@/components/TextBlock';
import { TitleColumn } from '@/components/TitleColumn';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { placeholderText } from '@/lib/text';
import { warnOnce } from '@/lib/warnOnce';
import type { DepartureStation as StationData } from '@/store/transforms';
import { DepartureStation } from '@/widgets/DepartureWidget/DepartureStation';
import { departureBoardSchema, departureOptionsSchema } from '@/widgets/DepartureWidget/options';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A public-transport departure board: a row of stations, each with its lines, each line with
 * its directions and their next departures. Port of `departure/departureWidget.vue` plus the
 * three child components under it.
 *
 * Used by `szeged_2`, `szeged_3` and `szeged_4`, all three on `all/departures`.
 *
 * The nested-object-to-array reshaping and the `HH:mm` formatting are `departureTransform`'s
 * (SYM-490), wired once by `WidgetSlot`. This widget renders what it is given.
 *
 * Three pieces of the legacy component are **not** ported, all of them dead: a `getCellClass`
 * function copy-pasted out of `bookingsWidget.vue` and never called, `columnNames` / `colors` /
 * `lineFlex` computeds over config keys this widget's configs do not carry, and a
 * `columnLabelOptions` font block for a label the template never renders. Dropping them is the
 * brief's instruction and changes nothing on screen.
 */

export function DepartureWidget({ componentId, options }: WidgetProps) {
  const parsed = departureOptionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `departure-widget-options:${componentId}`,
      '[signage] DepartureWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const opt = parsed.data;
  const stations = readBoard(data, componentId);

  return (
    <div className={className} style={style}>
      <div className="w-full flex justify-between px-2">
        <TitleColumn className="w-1/4" title={opt.title} titleFont={opt.titleFont}>
          {/*
           * Placeholder-only, exactly as legacy: `departureWidget.vue` computes the `{{var}}`
           * -> `0` form once and has no `watch` to update it, and by the time a payload
           * reaches this widget the transform has replaced it with a `DepartureStation[]`
           * that has no keys to substitute from. Same reasoning as `WeatherWidget`;
           * docs/MIGRATION-WIDGETS.md §3 lists both under the `'strip'` group.
           */}
          <TextBloc text={placeholderText(opt.textBloc.text)} font={opt.textBlocFont} />
        </TitleColumn>
        {/* Empty until the first message: docs/MIGRATION-WIDGETS.md §9, "no stations". */}
        <div className="flex flex-row flex-wrap justify-end w-3/4">
          {stations.map((station, index) => (
            <DepartureStation key={index} station={station} fonts={opt.component} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The board, or no stations before the first message / on a shape mismatch.
 *
 * The return type is the contract with `departureTransform`: if its `DepartureStation` ever
 * gains a field `departureBoardSchema` does not describe, this line stops compiling.
 */
function readBoard(data: unknown, componentId: string): StationData[] {
  if (data === undefined) return [];

  const parsed = departureBoardSchema.safeParse(data);
  if (!parsed.success) {
    warnOnce(
      `departure-widget-payload:${componentId}`,
      '[signage] DepartureWidget data is not a transformed board; keeping the empty state. %s',
      z.prettifyError(parsed.error),
    );
    return [];
  }
  return parsed.data;
}
